/**
 * Input Helper Utilities
 * Cross-browser compatible input handling for numeric fields
 * Fixes issues with leading zeros (065), placeholder behavior, and zero values
 */

/**
 * Format a numeric value for display in an input field
 * Handles edge cases:
 * - Empty/null/undefined → empty string (shows placeholder)
 * - Zero → empty string if shouldShowZero is false, "0" if true
 * - Number → string without leading zeros
 * 
 * @param {number|string|null|undefined} value - The value to format
 * @param {boolean} shouldShowZero - Whether to display "0" or empty string for zero values
 * @returns {string} - Formatted value for input field
 */
export const formatInputValue = (value, shouldShowZero = false) => {
  // Handle null/undefined/empty
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  // Convert to number
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  
  // Handle NaN
  if (isNaN(numValue)) {
    return '';
  }
  
  // Handle zero
  if (numValue === 0) {
    return shouldShowZero ? '0' : '';
  }
  
  // Return number as string (removes leading zeros automatically)
  return String(numValue);
};

/**
 * Parse user input from a numeric field
 * Handles:
 * - Empty string → 0 or null depending on allowNull
 * - Leading zeros → strips them (65 not 065)
 * - Non-numeric characters → strips them
 * - Decimal numbers → preserves decimals
 * 
 * @param {string} value - Raw input value
 * @param {boolean} allowNull - Whether to return null for empty string (vs 0)
 * @param {boolean} allowDecimals - Whether to allow decimal values
 * @returns {number|null} - Parsed numeric value
 */
export const parseInputValue = (value, allowNull = false, allowDecimals = true) => {
  // Handle empty
  if (value === '' || value === null || value === undefined) {
    return allowNull ? null : 0;
  }
  
  // Strip non-numeric characters (except decimal point and minus sign)
  let cleaned = String(value);
  
  if (allowDecimals) {
    // Allow digits, decimal point, and leading minus
    cleaned = cleaned.replace(/[^0-9.-]/g, '');
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
  } else {
    // Allow only digits and leading minus
    cleaned = cleaned.replace(/[^0-9-]/g, '');
  }
  
  // Parse to number
  const numValue = allowDecimals ? parseFloat(cleaned) : parseInt(cleaned, 10);
  
  // Return 0 for NaN
  if (isNaN(numValue)) {
    return allowNull ? null : 0;
  }
  
  return numValue;
};

/**
 * Handle onChange event for numeric input fields
 * Strips non-numeric characters and prevents leading zeros
 * 
 * @param {Event} e - Change event
 * @param {Function} callback - Callback function to update state
 * @param {Object} options - Configuration options
 * @returns {void}
 */
export const handleNumericInputChange = (e, callback, options = {}) => {
  const {
    allowDecimals = true,
    allowNegative = false,
    min = null,
    max = null
  } = options;
  
  let value = e.target.value;
  
  // Allow empty for clearing
  if (value === '') {
    callback('');
    return;
  }
  
  // Strip non-numeric (keep decimal and minus if allowed)
  if (allowDecimals && allowNegative) {
    value = value.replace(/[^0-9.-]/g, '');
  } else if (allowDecimals) {
    value = value.replace(/[^0-9.]/g, '');
  } else if (allowNegative) {
    value = value.replace(/[^0-9-]/g, '');
  } else {
    value = value.replace(/[^0-9]/g, '');
  }
  
  // Remove leading zeros (except for "0" and "0.")
  if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
    value = value.replace(/^0+/, '');
  }
  
  // Parse to number for validation
  const numValue = allowDecimals ? parseFloat(value) : parseInt(value, 10);
  
  // Apply min/max constraints
  if (min !== null && !isNaN(numValue) && numValue < min) {
    callback(String(min));
    return;
  }
  
  if (max !== null && !isNaN(numValue) && numValue > max) {
    callback(String(max));
    return;
  }
  
  // Update with cleaned value
  callback(value);
};

/**
 * Common input props for numeric fields
 * Use with spread operator: <input {...getNumericInputProps()} />
 * 
 * @param {Object} options - Configuration options
 * @returns {Object} - Props object for input element
 */
export const getNumericInputProps = (options = {}) => {
  const {
    allowDecimals = true,
    step = allowDecimals ? '0.01' : '1'
  } = options;
  
  return {
    type: 'text',
    inputMode: 'numeric',
    pattern: allowDecimals ? '[0-9]*\\.?[0-9]*' : '[0-9]*',
    autoComplete: 'off',
    spellCheck: false
  };
};

